import { toastError } from "@/utils/toastify";
import { useCallback } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

// Handle uploading files in chat input
// image: max number 10, max size 10mb
// video: max number 1, max size 500mb
// other files (pdf, etc.): max size 25mb
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_OTHER_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export { MAX_IMAGE_SIZE };

interface Props {
  enable?: boolean;
  handleFileAccepted: (file: File[]) => void;
}

export const useFileUpload = ({ enable = true, handleFileAccepted }: Props) => {
  // handle file rejected
  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    if (!rejectedFiles.length) return;

    const first = rejectedFiles[0];
    const firstError = first.errors[0];

    switch (firstError.code) {
      case "file-too-large":
        toastError("ファイルサイズが大きすぎます");
        break;
      case "file-invalid-type":
        toastError("無効なファイルタイプです");
        break;
      case "too-many-files":
        toastError("ファイルは12つまでアップロードできます");
        break;
      default:
        toastError(
          firstError.message || "ファイルのアップロードに失敗しました"
        );
    }
  }, []);

  // Validate file size + file type
  const validateFileSize = useCallback((file: File) => {
    const { size, type } = file;

    if (type.startsWith("image/")) {
      if (size > MAX_IMAGE_SIZE) {
        toastError("画像ファイルは10MB以下にしてください");
        return false;
      }
    } else if (type.startsWith("video/")) {
      if (size > MAX_VIDEO_SIZE) {
        toastError("動画ファイルは500MB以下にしてください");
        return false;
      }
    } else {
      if (size > MAX_OTHER_FILE_SIZE) {
        // other files max size is 25MB
        toastError("ファイルは25MB以下にしてください");
        return false;
      }
    }

    return true;
  }, []);

  // handle file accecpted
  const onDropAccepted = (files: File[]) => {
    // validate file size
    const validatedFiles = [] as File[];

    files.forEach(
      (file) => validateFileSize(file) && validatedFiles.push(file)
    );

    handleFileAccepted(validatedFiles);
  };

  // control file upload
  const { getInputProps, getRootProps, open } = useDropzone({
    onDropAccepted: (files) => onDropAccepted(files),
    onDropRejected,
    maxFiles: 12, // max 12 files for each select action
    // accept: {
    //   ...DROPZONE_ACCEPT.IMAGE.ALL,
    //   ...DROPZONE_ACCEPT.VIDEO.ALL,
    //   ...DROPZONE_ACCEPT.PDF,
    // },
    disabled: !enable,
  });

  return { getInputProps, getRootProps, open };
};
